import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ClipboardCheck, BookOpen, Rocket, ArrowRight,
  Briefcase, Globe2, Target, GraduationCap, Languages, Clock,
  ChevronLeft, ChevronRight,
  Volume2, VolumeX, RotateCcw, Play
} from 'lucide-react';
import { useCmsContent } from '../hooks/useCmsContent';
import { useLanguage } from '../contexts/LanguageContext';
import { parseHighlight } from '../utils/richText';
import { testimonialsFallback } from '../data/testimonialsFallback';
import { getFeaturedTestimonials } from '../services/testimonialsService';
import { TestimonialCard } from '../components/testimonials/TestimonialCard';

// Fallback image constants
const HERO_IMAGE_FB = "/assets/photos/site-updates/hero-cida.webp";
const HERO_VIDEO_POSTER_FB = "/assets/photos/site-updates/hero-final-touches.webp";
const VIDEO_CALL_FB = "/assets/photos/site-updates/teaching-small-groups.webp";
const COMMUNITY_IMAGE_FB = "/assets/photos/site-updates/online-worldwide-working.webp";
const COMMUNITY_IMAGE_2_FB = "/assets/photos/site-updates/aviation.webp";
const COMMUNITY_IMAGE_3_FB = "/assets/photos/site-updates/advanced-reading-strategies.webp";
// Real student photos (small compressed thumbnails), replacing the generic
// placeholder avatars — the CMS "avatar" fields still override these with a
// real upload if an admin sets one.
const COMMUNITY_AVATARS_FB = [
  "/assets/photos/testimonials/avatars/taeok.webp",
  "/assets/photos/testimonials/avatars/cintia.webp",
  "/assets/photos/testimonials/avatars/francielly.webp",
  "/assets/photos/testimonials/avatars/jamilly-hebio.webp",
];


const DEFAULT_FEATURES = [
  {
    badge: 'badge-red', badgeLabel: 'GLOBAL EXPERTISE', chip: 'General English',
    title: 'International Teaching Experience',
    description: 'Experience teaching students from different countries, cultures and learning backgrounds.',
    color: 'bg-[#D72B2B]',
  },
  {
    badge: 'badge-navy', badgeLabel: 'CAMBRIDGE CERTIFIED', chip: 'Cambridge Certified',
    title: 'Cambridge-Certified Expertise',
    description: 'Cambridge-certified expertise with advanced academic training in English Language Teaching.',
    color: 'bg-[#1A2E6E]',
  },
  {
    badge: 'badge-green', badgeLabel: 'ACADEMIC EXCELLENCE', chip: 'Academic Writing',
    title: "Master's Degree and PhD-Level Specialisation",
    description: "Academic excellence combining a Master's degree with ongoing doctoral research in English Language Teaching methodology.",
    color: 'bg-[#4CAF50]',
  },
  {
    badge: 'badge-orange', badgeLabel: 'NATIVE SPEAKER INSIGHT', chip: 'Portugues For Foreigners',
    title: 'Clear Explanations for Portuguese Speakers',
    description: 'A native Portuguese-speaking teacher who understands your challenges and explains English clearly.',
    color: 'bg-[#F5A623]',
  },
  {
    badge: 'badge-red', badgeLabel: 'RESULTS DRIVEN', chip: 'Speaking Development',
    title: 'Speaking Development',
    description: 'Practical lessons designed to build confidence in real conversations.',
    color: 'bg-[#D72B2B]',
  },
];

const DEFAULT_STEPS = [
  { icon: ClipboardCheck, step: '01', title: 'Test Your Level',                 description: 'Find out your current level through a quick and practical assessment.',                        color: 'bg-[#D72B2B]' },
  { icon: BookOpen,       step: '02', title: 'Get Your Recommended Plan',      description: 'We suggest the package that best fits your level and learning goals.',                         color: 'bg-[#4CAF50]' },
  { icon: Rocket,         step: '03', title: 'Book and Start Learning',        description: 'Choose your package, schedule your lessons, and begin learning with confidence.',              color: 'bg-[#F5A623]' }
];

const DEFAULT_PROGRAMS = [
  { title: 'General English',  subtitle: 'From Beginners to Advanced',    description: 'Build confidence in speaking, listening, reading, writing, grammar, and pronunciation.', color: 'bg-[#1A2E6E]', badge: 'badge-navy' },
  { title: 'Business English', subtitle: 'Professional Communication',    description: 'Communicate more professionally and confidently in meetings, emails, presentations, and interviews.', color: 'bg-[#F5A623]', badge: 'badge-orange' },
  { title: 'Academic English', subtitle: 'English for Academic Purposes', description: 'Prepare for essays, lectures, presentations, note-taking, and academic success.', color: 'bg-[#4CAF50]', badge: 'badge-green' },
];

const normalizeFeatureLabel = (value) => {
  const replacements = {
    International: 'General English',
    'Real Results': 'Speaking Development',
    "Master's + PhD": 'Academic Writing',
    'Master +PhD': 'Academic Writing',
    'Pt. Speaker': 'Portugues For Foreigners',
    'Pt speaker': 'Portugues For Foreigners',
  };

  return replacements[value] || value;
};



// ── Hero Video Player ────────────────────────────────────────────────────────
const HeroVideo = ({ src, poster }) => {
  const { t } = useLanguage();
  const wrapperRef = useRef(null);
  const videoRef = useRef(null);
  const autoPlayAttemptedRef = useRef(false);
  const [ended, setEnded]           = useState(false);
  const [isMuted, setIsMuted]       = useState(false);
  const [volume, setVolume]         = useState(0.6);
  const [showControls, setShowControls] = useState(false);
  const [isPlaying, setIsPlaying]   = useState(false);
  const [showPlayOverlay, setShowPlayOverlay] = useState(true);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // Prepare full-audio playback for when the video section enters the viewport.
    v.muted = false;
    v.volume = 0.6;
    autoPlayAttemptedRef.current = false;
    setEnded(false);
    setIsMuted(false);
    setVolume(0.6);
    setIsPlaying(false);
    setShowPlayOverlay(true);
  }, [src]);

  const playWithAudio = useCallback(async ({ restart = false } = {}) => {
    const v = videoRef.current;
    if (!v) return;

    if (restart) {
      v.currentTime = 0;
      setEnded(false);
    }

    v.muted = false;
    v.volume = 0.6;
    setIsMuted(false);
    setVolume(0.6);

    try {
      await v.play();
      setIsPlaying(true);
      setShowPlayOverlay(false);
    } catch {
      setIsPlaying(false);
      setShowPlayOverlay(true);
    }
  }, []);

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !autoPlayAttemptedRef.current) {
          autoPlayAttemptedRef.current = true;
          playWithAudio();
        }
      },
      { threshold: 0.55 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [playWithAudio]);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !isMuted;
    v.muted = next;
    setIsMuted(next);
  };

  const handleVolume = (e) => {
    const val = parseFloat(e.target.value);
    const v   = videoRef.current;
    if (!v) return;
    v.volume = val;
    setVolume(val);
    if (val === 0) { v.muted = true;  setIsMuted(true);  }
    else if (isMuted) { v.muted = false; setIsMuted(false); }
  };

  const replay = () => {
    playWithAudio({ restart: true });
  };

  return (
    <div
      ref={wrapperRef}
      className="relative rounded-xl overflow-hidden"
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster || undefined}
        muted={isMuted}
        preload="auto"
        playsInline
        onPlay={() => {
          setIsPlaying(true);
          setShowPlayOverlay(false);
        }}
        onPause={() => {
          setIsPlaying(false);
          if (!ended) setShowPlayOverlay(true);
        }}
        onEnded={() => {
          setEnded(true);
          setIsPlaying(false);
          setShowPlayOverlay(true);
        }}
        className="w-full rounded-xl block bg-[#1A2E6E]"
      />

      {/* Play stays available if autoplay with audio is blocked. */}
      {showPlayOverlay && !isPlaying && (
        <button
          type="button"
          onClick={() => playWithAudio({ restart: ended })}
          className="absolute inset-0 flex items-center justify-center text-white transition-colors"
          aria-label={ended ? t('home.video.replayVideo') : t('home.video.playVideo')}
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#1A2E6E]/60 shadow-lg backdrop-blur-sm ring-1 ring-white/35 transition-all hover:scale-105 hover:bg-[#1A2E6E]/75">
            <Play size={24} fill="currentColor" className="ml-0.5" />
          </span>
        </button>
      )}

      {/* Hover controls bar */}
      {showControls && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/65 to-transparent px-4 py-3 flex items-center gap-3 transition-all">
          {ended ? (
            <button
              onClick={replay}
              className="flex items-center gap-1.5 text-white hover:text-[#F5A623] text-xs font-semibold transition-colors flex-shrink-0"
            >
              <RotateCcw size={14} /> {t('home.video.replay')}
            </button>
          ) : (
            <button
              onClick={toggleMute}
              className="text-white hover:text-[#F5A623] transition-colors flex-shrink-0"
              aria-label={isMuted ? t('home.video.unmute') : t('home.video.mute')}
            >
              {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
          )}
          <input
            type="range"
            min="0" max="1" step="0.05"
            value={isMuted ? 0 : volume}
            onChange={handleVolume}
            className="w-20 h-1 accent-[#F5A623] cursor-pointer"
            aria-label={t('home.video.volume')}
          />
        </div>
      )}
    </div>
  );
};

const HeroVideoCard = ({ src, poster, tone = 'dark' }) => {
  const { t } = useLanguage();
  const isLight = tone === 'light';

  return (
    <div className={isLight ? 'bg-white rounded-2xl border border-[#E8EAF0] p-4 shadow-xl shadow-[#1A2E6E]/10' : 'frosted-panel p-4'}>
      <HeroVideo
        src={src}
        poster={poster}
      />
      <div className="mt-4 flex items-center gap-3">
        <div className="w-3 h-3 rounded-full bg-[#4CAF50] animate-pulse" />
        <span className={`${isLight ? 'text-[#1A2E6E]' : 'text-white'} text-sm font-medium`}>
          {t('home.video.liveLessons')}
        </span>
      </div>
    </div>
  );
};

export const Home = () => {
  const cms = useCmsContent('home');
  const { t, tList, language } = useLanguage();
  const [diffSlide, setDiffSlide] = useState(0);
  const [testimonials, setTestimonials] = useState(testimonialsFallback.slice(0, 3));

  const translatedBadgeLabels = [
    t('home.badges.globalExpertise'),
    t('home.badges.cambridgeCertified'),
    t('home.badges.academicExcellence'),
    t('home.badges.nativeSpeakerInsight'),
    t('home.badges.resultsDriven'),
  ];

  const trustItems = [
    { icon: Briefcase,     label: t('home.trust.businessName'),   lines: [t('home.trust.businessLine1'), t('home.trust.businessLine2')],     color: 'text-[#D72B2B]' },
    { icon: Globe2,        label: t('home.trust.generalName'),    lines: [t('home.trust.generalLine1'), t('home.trust.generalLine2')],       color: 'text-[#1A2E6E]' },
    { icon: Target,        label: t('home.trust.speakingName'),   lines: [t('home.trust.speakingLine1'), t('home.trust.speakingLine2')],     color: 'text-[#4CAF50]' },
    { icon: GraduationCap, label: t('home.trust.academicName'),   lines: [t('home.trust.academicLine1'), t('home.trust.academicLine2')],     color: 'text-[#F5A623]' },
    { icon: Languages,     label: t('home.trust.portugueseName'), lines: [t('home.trust.portugueseLine1'), t('home.trust.portugueseLine2')], color: 'text-[#D72B2B]' },
    { icon: Clock,         label: t('home.trust.oneToOneName'),   lines: [t('home.trust.oneToOneLine1'), t('home.trust.oneToOneLine2')],     color: 'text-[#1A2E6E]' },
  ];

  // Hero
  const heroBadge      = cms.get('hero', 'badge',         t('home.hero.badge'));
  const heroBrand      = cms.get('hero', 'brand',         t('home.hero.brand'));
  const heroTitle      = cms.get('hero', 'title',         t('home.hero.title'));
  const heroSubtitle   = cms.get('hero', 'subtitle',      t('home.hero.subtitle'));
  const heroPrimary    = cms.get('hero', 'cta_primary',   t('home.hero.ctaPrimary'));
  const heroSecondary  = cms.get('hero', 'cta_secondary', t('home.hero.ctaSecondary'));
  const heroImageRaw   = cms.get('hero', 'image',         HERO_IMAGE_FB);
  const heroImage      = [
    '/assets/photos/site-updates/hero-final-touches.webp',
    '/assets/photos/site-updates/teaching-hero.webp',
  ].includes(heroImageRaw) ? HERO_IMAGE_FB : heroImageRaw;
  const heroVideo      = cms.get('hero', 'video',         '/assets/videos/hero-teacher.mp4');
  const heroVideoPoster = cms.get('hero', 'video_poster', HERO_VIDEO_POSTER_FB);

  // Why Choose Us
  const whyBadge    = cms.get('features_header', 'badge',    t('home.why.badge'));
  const whyTitle    = cms.get('features_header', 'title',    t('home.why.title'));
  const whyBody     = cms.get('features_header', 'body',     t('home.why.body'));
  const whyBody2    = cms.get('features_header', 'body2',    t('home.why.body2'));
  const whyClosing   = cms.get('features_header', 'closing',  t('home.why.closing'));
  const whyDiffLabel = cms.get('features_header', 'subtitle', t('home.why.differentLabel'));
  const localizedFeatures = tList('home.featuresList');
  const cmsFeatures  = cms.getList('features', []);
  const features     = DEFAULT_FEATURES.map((f, i) => ({
    ...f,
    badgeLabel:  translatedBadgeLabels[i] || f.badgeLabel,
    title:       normalizeFeatureLabel(cmsFeatures[i]?.title || localizedFeatures[i]?.title || f.title),
    description: cmsFeatures[i]?.description || localizedFeatures[i]?.description || f.description,
  }));

  // Community
  const communityBadge  = cms.get('community', 'badge',  t('home.community.badge'));
  const communityTitle  = cms.get('community', 'title',  t('home.community.title'));
  const communityBody   = cms.get('community', 'body',   t('home.community.body'));
  const communityStat   = cms.get('community', 'stat',   t('home.community.stat'));
  const communityImage  = cms.get('community', 'image',  COMMUNITY_IMAGE_FB);
  const communityImage2Raw = cms.get('community', 'image2', COMMUNITY_IMAGE_2_FB);
  const communityImage2 = [VIDEO_CALL_FB, '/assets/photos/site-updates/professional-good-one.webp'].includes(communityImage2Raw)
    ? COMMUNITY_IMAGE_2_FB
    : communityImage2Raw;
  const communityImage3Raw = cms.get('community', 'image3', COMMUNITY_IMAGE_3_FB);
  const communityImage3 = communityImage3Raw === '/assets/photos/site-updates/teaching-style.webp'
    ? COMMUNITY_IMAGE_3_FB
    : communityImage3Raw;
  const communityAvatars = [
    cms.get('community', 'person1_avatar', COMMUNITY_AVATARS_FB[0]),
    cms.get('community', 'person2_avatar', COMMUNITY_AVATARS_FB[1]),
    cms.get('community', 'person3_avatar', COMMUNITY_AVATARS_FB[2]),
    cms.get('community', 'person4_avatar', COMMUNITY_AVATARS_FB[3]),
  ].filter(Boolean);

  // How It Works
  const howBadge    = cms.get('how_it_works', 'badge',    t('home.howItWorks.badge'));
  const howTitle    = cms.get('how_it_works', 'title',    t('home.howItWorks.title'));
  const howSubtitle = cms.get('how_it_works', 'subtitle', t('home.howItWorks.subtitle'));
  const localizedSteps = tList('home.stepsList');
  const cmsSteps    = cms.getList('steps', []);
  const steps = DEFAULT_STEPS.map((s, i) => ({
    ...s,
    step:        cmsSteps[i]?.number      || s.step,
    title:       cmsSteps[i]?.title       || localizedSteps[i]?.title       || s.title,
    description: cmsSteps[i]?.description || localizedSteps[i]?.description || s.description,
  }));

  // Programs
  const progBadge    = cms.get('programs', 'badge',    t('home.programsSection.badge'));
  const progTitle    = cms.get('programs', 'title',    t('home.programsSection.title'));
  const progSubtitle = cms.get('programs', 'subtitle', t('home.programsSection.subtitle'));
  const progCta      = cms.get('programs', 'cta',      t('home.programsSection.cta'));
  const localizedPrograms = tList('home.programsList');
  const cmsPrograms  = cms.getList('program_cards', []);
  const programs = DEFAULT_PROGRAMS.map((p, i) => ({
    ...p,
    title:       cmsPrograms[i]?.title       || localizedPrograms[i]?.title       || p.title,
    subtitle:    cmsPrograms[i]?.subtitle    || localizedPrograms[i]?.subtitle    || p.subtitle,
    description: cmsPrograms[i]?.description || localizedPrograms[i]?.description || p.description,
  }));

  // Testimonials
  const tmBadge    = cms.get('testimonials_header', 'badge',    t('home.testimonialsSection.badge'));
  const tmTitle    = cms.get('testimonials_header', 'title',    t('home.testimonialsSection.title'));
  const tmSubtitle = cms.get('testimonials_header', 'subtitle', t('home.testimonialsSection.subtitle'));

  useEffect(() => {
    if (features.length < 2) return undefined;
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return undefined;

    const timer = window.setInterval(() => {
      setDiffSlide((slide) => (slide + 1) % features.length);
    }, 8000);

    return () => window.clearInterval(timer);
  }, [features.length]);

  useEffect(() => {
    getFeaturedTestimonials(3, language)
      .then((data) => {
        if (data?.length) setTestimonials(data);
      })
      .catch(() => setTestimonials(testimonialsFallback.slice(0, 3)));
  }, [language]);

  // Bottom CTA
  const ctaTitle     = cms.get('cta', 'title',         t('home.ctaSection.title'));
  const ctaBody      = cms.get('cta', 'body',          t('home.ctaSection.body'));
  const ctaPrimary   = cms.get('cta', 'cta_primary',   t('home.ctaSection.primary'));
  const ctaSecondary = cms.get('cta', 'cta_secondary', t('home.ctaSection.secondary'));

  return (
    <main className="overflow-hidden" data-testid="home-page">
      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-[#162344] sm:flex sm:min-h-screen sm:items-center sm:bg-[#1A2E6E]" style={{ paddingTop: '96px' }} data-testid="hero-section">
        <div className="absolute inset-0 z-0 hidden sm:block">
          <img
            src={heroImage}
            alt={t('home.heroImageAlt')}
            className="w-full h-full object-cover"
            style={{ objectPosition: '85% center' }}
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1A2E6E]/95 via-[#1A2E6E]/85 to-[#1A2E6E]/75 lg:hidden" />
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[470px] overflow-hidden sm:hidden">
          <img
            src={heroImage}
            alt=""
            aria-hidden="true"
            className="absolute inset-y-0 left-1/2 h-full w-[125%] max-w-none -translate-x-[58%] object-cover object-[100%_center]"
            fetchPriority="high"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, #162344 0%, rgba(22, 35, 68, 0.78) 6%, rgba(22, 35, 68, 0.28) 12%, rgba(22, 35, 68, 0) 20%)',
            }}
          />
        </div>
        <div className="container-custom relative z-10 w-full pb-[420px] pt-12 sm:py-20 lg:py-0">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mx-auto w-full max-w-3xl text-center lg:mx-0 lg:w-1/2 lg:max-w-none lg:pr-12 lg:text-left"
          >
            <span className="badge-orange inline-block mb-6">{heroBadge}</span>
            <h1 className="font-sora text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-3 uppercase tracking-normal">
              {heroBrand}
            </h1>
            <p className="font-sora text-2xl sm:text-3xl text-white font-semibold mb-6">{parseHighlight(heroTitle)}</p>
            <p className="text-lg text-white/90 mb-8 leading-relaxed max-w-2xl">{heroSubtitle}</p>
            <div className="flex flex-col gap-4 sm:mb-8 sm:flex-row sm:flex-wrap sm:justify-center lg:justify-start">
              <Link to="/level-test" className="btn-primary flex w-full items-center justify-center gap-2 sm:w-auto" data-testid="hero-level-test-btn">
                {heroPrimary} <ArrowRight size={18} />
              </Link>
              <Link to="/book-trial" className="w-full rounded-full border border-white/30 bg-white/10 px-8 py-3.5 text-center font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/20 sm:w-auto" data-testid="hero-book-trial-btn">
                {heroSecondary}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Trust Strip ───────────────────────────────────────────────── */}
      <section className="bg-[#F5F7FA] border-t border-b border-[#E8EAF0]" data-testid="trust-strip">
        <div className="container-custom">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 lg:gap-4 py-8">
            {trustItems.map((item, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="flex min-w-0 flex-col items-center text-center">
                <div className={`w-11 h-11 flex items-center justify-center mb-2 ${item.color}`}>
                  <item.icon size={40} strokeWidth={1.5} />
                </div>
                <span className="flex min-h-[2.25rem] flex-col items-center justify-start text-sm font-semibold leading-tight text-[#1A2E6E]" aria-label={item.label}>
                  {item.lines.map((line) => (
                    <span key={line}>{line}</span>
                  ))}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Choose Us ─────────────────────────────────────────────── */}
      <section className="section-padding bg-watercolor-soft relative overflow-hidden" data-testid="why-choose-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* Left: text content */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="badge-navy mb-4 inline-block">{whyBadge}</span>
              <h2 className="font-sora text-3xl md:text-4xl font-bold text-[#1A2E6E] mb-6">
                {parseHighlight(whyTitle)}
              </h2>
              <p className="text-[#1A2E6E]/70 text-lg mb-6 leading-relaxed">{whyBody}</p>
              <p className="text-[#1A2E6E]/70 text-lg mb-8 leading-relaxed">{whyBody2}</p>
              <div className="flex flex-wrap items-center gap-3">
                <span className="block w-8 h-0.5 bg-[#D72B2B] rounded-full flex-shrink-0" />
                <Link
                  to="/contact"
                  className="font-sora font-semibold text-[#D72B2B] text-base hover:underline underline-offset-4 decoration-[#D72B2B]/50 transition-all"
                >
                  {whyClosing}
                </Link>
                <span className="block w-8 h-0.5 bg-[#D72B2B] rounded-full flex-shrink-0" />
              </div>
            </motion.div>

            {/* Right: video card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <HeroVideoCard src={heroVideo} poster={heroVideoPoster} tone="light" />
            </motion.div>

          </div>

          {/* What makes us different — carousel */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-14 pt-12 border-t border-[#1A2E6E]/10"
          >
            <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between">
              <p className="font-sora font-semibold text-[#1A2E6E]/50 text-sm uppercase tracking-widest">
                {whyDiffLabel}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setDiffSlide(s => (s - 1 + features.length) % features.length)}
                  className="w-9 h-9 rounded-full border border-[#1A2E6E]/20 flex items-center justify-center hover:bg-[#1A2E6E]/5 transition-colors"
                  aria-label={t('home.carousel.previous')}
                >
                  <ChevronLeft size={18} className="text-[#1A2E6E]/60" />
                </button>
                <button
                  onClick={() => setDiffSlide(s => (s + 1) % features.length)}
                  className="w-9 h-9 rounded-full border border-[#1A2E6E]/20 flex items-center justify-center hover:bg-[#1A2E6E]/5 transition-colors"
                  aria-label={t('home.carousel.next')}
                >
                  <ChevronRight size={18} className="text-[#1A2E6E]/60" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[0, 1, 2].map(offset => {
                const feature = features[(diffSlide + offset) % features.length];
                return (
                  <motion.div
                    key={diffSlide + offset}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: offset * 0.05 }}
                    className={`glass-card p-8 flex flex-col hover:shadow-xl transition-all duration-300 card-accent-hover card-accent-navy ${offset > 0 ? 'hidden md:flex' : 'flex'}`}
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div className={`w-2 h-12 ${feature.color} rounded-full flex-shrink-0`} />
                      <div>
                        <span className={`${feature.badge} text-xs mb-2 inline-block`}>{feature.badgeLabel}</span>
                        <h3 className="font-sora font-semibold text-xl text-[#1A2E6E]">{feature.title}</h3>
                      </div>
                    </div>
                    <p className="text-[#1A2E6E]/70 leading-relaxed ml-6 flex-grow">{feature.description}</p>
                  </motion.div>
                );
              })}
            </div>
            <div className="mt-6 flex items-center justify-center gap-2" aria-label={t('home.carousel.navigation')}>
              {features.map((feature, index) => (
                <button
                  key={feature.title}
                  type="button"
                  onClick={() => setDiffSlide(index)}
                  className={`h-2.5 rounded-full transition-all duration-300 ${
                    index === diffSlide
                      ? 'w-8 bg-[#1A2E6E]'
                      : 'w-2.5 bg-[#1A2E6E]/20 hover:bg-[#1A2E6E]/40'
                  }`}
                  aria-label={t('home.carousel.show', { title: feature.title })}
                  aria-current={index === diffSlide ? 'true' : undefined}
                />
              ))}
            </div>
          </motion.div>

        </div>
      </section>

      {/* ── Global Community ──────────────────────────────────────────── */}
      <section className="section-padding bg-cream-gradient relative overflow-hidden">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="photo-container h-40 sm:h-48"><img src={communityImage} alt={t('home.community.multiculturalStudents')} className="photo-fit-card" /></div>
              <div className="photo-container mt-4 h-40 sm:mt-8 sm:h-48"><img src={communityImage2} alt={t('home.community.aviationLesson')} className="photo-fit-card" /></div>
              <div className="photo-container col-span-2 aspect-video">
                <img
                  src={communityImage3}
                  alt={t('home.community.readingStrategiesLesson')}
                  className="photo-fit-card"
                  style={{ objectPosition: 'center 22%' }}
                />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="badge-green mb-4 inline-block">{communityBadge}</span>
              <h2 className="font-sora text-3xl md:text-4xl font-bold text-[#1A2E6E] mb-6">{parseHighlight(communityTitle)}</h2>
              <p className="text-[#1A2E6E]/70 text-lg mb-6 leading-relaxed">{communityBody}</p>
              <div className="flex items-center gap-4">
                <div className="flex -space-x-3">
                  {communityAvatars.map((avatar, index) => (
                    <img
                      key={`${avatar}-${index}`}
                      src={avatar}
                      alt={t('common.studentAlt', { index: index + 1 })}
                      className="w-12 h-12 rounded-full border-3 border-white object-cover"
                    />
                  ))}
                </div>
                <p className="text-[#1A2E6E]/70 text-sm font-medium">{communityStat}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── How It Works ──────────────────────────────────────────────── */}
      <section className="section-padding bg-paper-lines relative" data-testid="how-it-works-section">
        <div className="container-custom">
          <motion.div className="text-center max-w-2xl mx-auto mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="badge-red mb-4 inline-block">{howBadge}</span>
            <h2 className="font-sora text-3xl md:text-4xl font-bold text-[#1A2E6E] mb-4">{parseHighlight(howTitle)}</h2>
            <p className="text-[#1A2E6E]/70 text-lg">{howSubtitle}</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.15 }} className="relative">
                <div className="glass-card p-8 h-full card-accent-hover card-accent-navy">
                  <span className="text-6xl font-sora font-bold text-[#1A2E6E]/5 absolute top-6 right-6">{step.step}</span>
                  <div className={`w-14 h-14 rounded-xl ${step.color} flex items-center justify-center mb-6 shadow-lg`}>
                    <step.icon size={26} className="text-white" />
                  </div>
                  <h3 className="font-sora font-semibold text-xl text-[#1A2E6E] mb-3">{step.title}</h3>
                  <p className="text-[#1A2E6E]/70 leading-relaxed">{step.description}</p>
                </div>
                {index < steps.length - 1 && <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-[#1A2E6E]/20" />}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Popular Programs ──────────────────────────────────────────── */}
      <section className="section-padding bg-watercolor-soft" data-testid="programs-section">
        <div className="container-custom">
          <motion.div className="text-center max-w-2xl mx-auto mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="badge-orange mb-4 inline-block">{progBadge}</span>
            <h2 className="font-sora text-3xl md:text-4xl font-bold text-[#1A2E6E] mb-4">{parseHighlight(progTitle)}</h2>
            <p className="text-[#1A2E6E]/70 text-lg">{progSubtitle}</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {programs.map((program, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="glass-card p-6 sm:p-8 flex flex-col card-accent-hover card-accent-navy hover:shadow-xl transition-all duration-400">
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-2 h-12 ${program.color} rounded-full flex-shrink-0`} />
                  <div>
                    <span className={`${program.badge} text-xs mb-2 inline-block`}>{program.subtitle}</span>
                    <h3 className="font-sora font-semibold text-2xl text-[#1A2E6E]">{program.title}</h3>
                  </div>
                </div>
                <p className="text-[#1A2E6E]/70 leading-relaxed mb-6 ml-6 flex-grow">{program.description}</p>
                <Link to="/courses" className="inline-flex items-center gap-2 text-[#1A2E6E] font-medium hover:gap-3 transition-all ml-6 mt-auto">
                  {t('home.learnMore')} <ArrowRight size={16} />
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/courses" className="btn-navy inline-flex items-center gap-2" data-testid="view-all-courses-btn">
              {progCta} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Testimonials ──────────────────────────────────────────────── */}
      <section className="section-padding bg-chalk-dark relative" data-testid="testimonials-section">
        <div className="container-custom relative z-10">
          <motion.div className="text-center max-w-2xl mx-auto mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="badge-orange mb-4 inline-block">{tmBadge}</span>
            <h2 className="font-sora text-3xl md:text-4xl font-bold text-white mb-4">{parseHighlight(tmTitle)}</h2>
            <p className="text-white/70 text-lg">{tmSubtitle}</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, index) => (
              <motion.div key={t.id || t.slug || index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.12 }}>
                <TestimonialCard testimonial={t} index={index} dark />
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/testimonials" className="text-[#F5A623] hover:text-white font-medium inline-flex items-center gap-2 transition-colors">
              {t('home.viewAllTestimonials')} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────────────── */}
      <section className="section-padding bg-gradient-to-br from-[#D72B2B] to-[#B71C1C] relative" data-testid="cta-section">
        <div className="absolute inset-0 dot-pattern" />
        <div className="container-custom text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-2xl lg:max-w-none mx-auto">
            <h2 className="font-sora text-3xl md:text-4xl font-bold text-white mb-4">{parseHighlight(ctaTitle)}</h2>
            <p className="text-white/90 text-lg mb-8">{ctaBody}</p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row sm:flex-wrap">
              <Link to="/level-test" className="w-full rounded-full bg-white px-8 py-3.5 text-center font-semibold text-[#D72B2B] shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl sm:w-auto" data-testid="cta-level-test-btn">
                {ctaPrimary}
              </Link>
              <Link to="/book-trial" className="w-full rounded-full border-2 border-white/40 bg-transparent px-8 py-3.5 text-center font-semibold text-white transition-all duration-300 hover:bg-white/10 sm:w-auto" data-testid="cta-book-trial-btn">
                {ctaSecondary}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};
